import React from 'react';
import { Link } from 'react-router-dom';

const WhoItsFor: React.FC = () => {
  return (
    <main className="who-its-for-page">
      {/* Hero - Clean Typographic Focus */}
      <section className="wif-hero-v2">
        <div className="wif-hero-v2-bg">
          {/* Animated gradient orbs */}
          <div className="wif-orb orb-1"></div>
          <div className="wif-orb orb-2"></div>
          <div className="wif-orb orb-3"></div>
        </div>
        <div className="container wif-hero-v2-content">
          <div className="wif-hero-v2-text">
            <span className="wif-hero-v2-label">Who It's For</span>
            <h1>
              <span className="wif-title-line">Data-rich.</span>
              <span className="wif-title-line">Growth-ready.</span>
              <span className="wif-title-line wif-title-accent">Under-instrumented.</span>
            </h1>
            <p className="wif-hero-v2-subtitle">
              We work best with growing companies that are data-rich but operationally 
              under-instrumented — companies ready to operate like mid-sized ones.
            </p>
          </div>
          <div className="wif-hero-v2-visual">
            <div className="wif-data-stream">
              <div className="wif-stream-line sl-1"></div>
              <div className="wif-stream-line sl-2"></div>
              <div className="wif-stream-line sl-3"></div>
              <div className="wif-stream-line sl-4"></div>
              <div className="wif-stream-line sl-5"></div>
              <div className="wif-stream-node sn-1"></div>
              <div className="wif-stream-node sn-2"></div>
              <div className="wif-stream-node sn-3"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Audience Section - "Great Fit" - Modern Cards */}
      <section className="section audience-section-v3">
        <div className="container">
          <div className="audience-header-v3">
            <span className="audience-label">Perfect Match</span>
            <h2>You're a great fit if you're...</h2>
          </div>

          <div className="audience-grid-v3">
            {/* 10-250 Employees */}
            <div className="audience-card-v3" style={{ '--delay': '0s', '--accent': 'var(--color-turquoise)' } as React.CSSProperties}>
              <div className="audience-card-v3-number">01</div>
              <div className="audience-card-v3-content">
                <h3>10–250 Employees</h3>
                <p>Growing companies that have outgrown spreadsheets but aren't ready for an in-house data team.</p>
              </div>
              <div className="audience-card-v3-bar"></div>
            </div>

            {/* Growing Quickly */}
            <div className="audience-card-v3" style={{ '--delay': '0.1s', '--accent': 'var(--color-green)' } as React.CSSProperties}>
              <div className="audience-card-v3-number">02</div>
              <div className="audience-card-v3-content">
                <h3>Growing Quickly</h3>
                <p>Scaling businesses where decisions need to keep pace with growth — and manual processes are slowing you down.</p>
              </div>
              <div className="audience-card-v3-bar"></div>
            </div>

            {/* Data-Rich */}
            <div className="audience-card-v3" style={{ '--delay': '0.2s', '--accent': 'var(--color-yellow)' } as React.CSSProperties}>
              <div className="audience-card-v3-number">03</div>
              <div className="audience-card-v3-content">
                <h3>Data-Rich</h3>
                <p>You have data in various systems — CRM, payments, marketing tools, product — but it's not working together.</p>
              </div>
              <div className="audience-card-v3-bar"></div>
            </div>

            {/* Outcome-Focused */}
            <div className="audience-card-v3" style={{ '--delay': '0.3s', '--accent': 'var(--color-turquoise)' } as React.CSSProperties}>
              <div className="audience-card-v3-number">04</div>
              <div className="audience-card-v3-content">
                <h3>Outcome-Focused</h3>
                <p>You care about business results, not technical complexity. You want solutions that work, not science projects.</p>
              </div>
              <div className="audience-card-v3-bar"></div>
            </div>

            {/* Ready to Level Up */}
            <div className="audience-card-v3" style={{ '--delay': '0.4s', '--accent': 'var(--color-green)' } as React.CSSProperties}>
              <div className="audience-card-v3-number">05</div>
              <div className="audience-card-v3-content">
                <h3>Ready to Level Up</h3>
                <p>You know better data could help you make better decisions — you just need help making it happen.</p>
              </div>
              <div className="audience-card-v3-bar"></div>
            </div>

            {/* No Data Team */}
            <div className="audience-card-v3" style={{ '--delay': '0.5s', '--accent': 'var(--color-yellow)' } as React.CSSProperties}>
              <div className="audience-card-v3-number">06</div>
              <div className="audience-card-v3-content">
                <h3>No Data Team (Yet)</h3>
                <p>You don't have in-house data staff, or you have one person who's stretched thin and needs support.</p>
              </div>
              <div className="audience-card-v3-bar"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Signs Section - Pain Points */}
      <section className="section signs-section-v2">
        <div className="signs-bg">
          {/* Subtle pattern */}
          <div className="signs-pattern"></div>
        </div>
        <div className="container signs-container">
          <div className="signs-header-v2">
            <div className="accent-bar accent-bar-center mb-lg"></div>
            <h2>Signs you might need us</h2>
            <p>Sound familiar? These are the situations we help with most.</p>
          </div>

          <div className="signs-grid-v2">
            {/* Report Chaos */}
            <div className="sign-card-v2" style={{ '--delay': '0s' } as React.CSSProperties}>
              <div className="sign-icon-v2" data-sign="chaos">
                <svg viewBox="0 0 48 48">
                  {/* Scattered papers */}
                  <rect className="paper p1" x="6" y="8" width="16" height="20" rx="2" fill="var(--color-pearl)" stroke="var(--color-turquoise)" strokeWidth="1.5" transform="rotate(-15 14 18)"/>
                  <rect className="paper p2" x="16" y="12" width="16" height="20" rx="2" fill="var(--color-pearl)" stroke="var(--color-turquoise)" strokeWidth="1.5" transform="rotate(10 24 22)"/>
                  <rect className="paper p3" x="26" y="6" width="16" height="20" rx="2" fill="var(--color-pearl)" stroke="var(--color-turquoise)" strokeWidth="1.5" transform="rotate(25 34 16)"/>
                  {/* Exclamation */}
                  <circle className="alert-circle" cx="24" cy="38" r="6" fill="var(--color-yellow)"/>
                  <text x="24" y="42" textAnchor="middle" fontSize="10" fontWeight="bold" fill="var(--color-navy)">!</text>
                </svg>
              </div>
              <div className="sign-content">
                <h4>Report chaos</h4>
                <p>You spend hours each week pulling together reports from different systems.</p>
              </div>
            </div>

            {/* Trust Issues */}
            <div className="sign-card-v2" style={{ '--delay': '0.1s' } as React.CSSProperties}>
              <div className="sign-icon-v2" data-sign="trust">
                <svg viewBox="0 0 48 48">
                  {/* Two conflicting numbers */}
                  <circle className="num-bubble nb1" cx="14" cy="18" r="10" fill="var(--color-seafoam)"/>
                  <text className="num-text" x="14" y="22" textAnchor="middle" fontSize="12" fontWeight="bold" fill="var(--color-navy)">42</text>
                  <circle className="num-bubble nb2" cx="34" cy="18" r="10" fill="var(--color-seafoam)"/>
                  <text className="num-text" x="34" y="22" textAnchor="middle" fontSize="12" fontWeight="bold" fill="var(--color-navy)">67</text>
                  {/* Question marks */}
                  <text className="question-mark qm1" x="24" y="38" textAnchor="middle" fontSize="16" fontWeight="bold" fill="var(--color-yellow)">?</text>
                  <text className="question-mark qm2" x="16" y="42" textAnchor="middle" fontSize="10" fill="var(--color-turquoise)">?</text>
                  <text className="question-mark qm3" x="32" y="44" textAnchor="middle" fontSize="10" fill="var(--color-turquoise)">?</text>
                </svg>
              </div>
              <div className="sign-content">
                <h4>Trust issues</h4>
                <p>Different people get different numbers when answering the same question.</p>
              </div>
            </div>

            {/* Gut Decisions */}
            <div className="sign-card-v2" style={{ '--delay': '0.2s' } as React.CSSProperties}>
              <div className="sign-icon-v2" data-sign="gut">
                <svg viewBox="0 0 48 48">
                  {/* Dice */}
                  <rect className="dice" x="10" y="10" width="28" height="28" rx="4" fill="var(--color-pearl)" stroke="var(--color-navy)" strokeWidth="2"/>
                  <circle className="dot d1" cx="18" cy="18" r="2.5" fill="var(--color-navy)"/>
                  <circle className="dot d2" cx="30" cy="18" r="2.5" fill="var(--color-navy)"/>
                  <circle className="dot d3" cx="24" cy="24" r="2.5" fill="var(--color-navy)"/>
                  <circle className="dot d4" cx="18" cy="30" r="2.5" fill="var(--color-navy)"/>
                  <circle className="dot d5" cx="30" cy="30" r="2.5" fill="var(--color-navy)"/>
                  {/* Uncertainty swoosh */}
                  <path className="uncertainty" d="M6,40 Q24,32 42,40" fill="none" stroke="var(--color-yellow)" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 2"/>
                </svg>
              </div>
              <div className="sign-content">
                <h4>Gut decisions</h4>
                <p>Important decisions are made on intuition because data is too hard to access.</p>
              </div>
            </div>

            {/* Spreadsheet Sprawl */}
            <div className="sign-card-v2" style={{ '--delay': '0.3s' } as React.CSSProperties}>
              <div className="sign-icon-v2" data-sign="spreadsheet">
                <svg viewBox="0 0 48 48">
                  {/* Tangled grid */}
                  <rect className="sheet-bg" x="8" y="8" width="32" height="32" rx="2" fill="var(--color-pearl)"/>
                  <line className="grid-line gl-h1" x1="8" y1="16" x2="40" y2="16" stroke="var(--color-turquoise)" strokeWidth="1"/>
                  <line className="grid-line gl-h2" x1="8" y1="24" x2="40" y2="24" stroke="var(--color-turquoise)" strokeWidth="1"/>
                  <line className="grid-line gl-h3" x1="8" y1="32" x2="40" y2="32" stroke="var(--color-turquoise)" strokeWidth="1"/>
                  <line className="grid-line gl-v1" x1="18" y1="8" x2="18" y2="40" stroke="var(--color-turquoise)" strokeWidth="1"/>
                  <line className="grid-line gl-v2" x1="28" y1="8" x2="28" y2="40" stroke="var(--color-turquoise)" strokeWidth="1"/>
                  {/* Tangle overlay */}
                  <path className="tangle" d="M12,12 Q20,28 14,36 M22,10 Q32,20 20,30 M34,14 Q26,24 36,34" fill="none" stroke="var(--color-yellow)" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="sign-content">
                <h4>Spreadsheet sprawl</h4>
                <p>Critical business data lives in spreadsheets that only one person understands.</p>
              </div>
            </div>

            {/* Reactive Mode */}
            <div className="sign-card-v2" style={{ '--delay': '0.4s' } as React.CSSProperties}>
              <div className="sign-icon-v2" data-sign="reactive">
                <svg viewBox="0 0 48 48">
                  {/* Alarm clock */}
                  <circle className="clock-face" cx="24" cy="26" r="14" fill="var(--color-pearl)" stroke="var(--color-navy)" strokeWidth="2"/>
                  <circle className="clock-center" cx="24" cy="26" r="2" fill="var(--color-navy)"/>
                  <line className="clock-hand hour" x1="24" y1="26" x2="24" y2="18" stroke="var(--color-navy)" strokeWidth="2" strokeLinecap="round"/>
                  <line className="clock-hand minute" x1="24" y1="26" x2="30" y2="26" stroke="var(--color-navy)" strokeWidth="2" strokeLinecap="round"/>
                  {/* Alarm bells */}
                  <circle className="bell bell-l" cx="14" cy="14" r="4" fill="var(--color-yellow)"/>
                  <circle className="bell bell-r" cx="34" cy="14" r="4" fill="var(--color-yellow)"/>
                  {/* Alert waves */}
                  <path className="alert-wave aw1" d="M6,10 Q10,6 14,10" fill="none" stroke="var(--color-turquoise)" strokeWidth="1.5" strokeLinecap="round"/>
                  <path className="alert-wave aw2" d="M34,10 Q38,6 42,10" fill="none" stroke="var(--color-turquoise)" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="sign-content">
                <h4>Reactive mode</h4>
                <p>You're always reacting to problems instead of anticipating them.</p>
              </div>
            </div>

            {/* Manual Everything */}
            <div className="sign-card-v2" style={{ '--delay': '0.5s' } as React.CSSProperties}>
              <div className="sign-icon-v2" data-sign="manual">
                <svg viewBox="0 0 48 48">
                  {/* Gears grinding */}
                  <g className="gear gear-big">
                    <circle cx="20" cy="24" r="10" fill="none" stroke="var(--color-turquoise)" strokeWidth="3"/>
                    <circle cx="20" cy="24" r="4" fill="var(--color-navy)"/>
                    {/* Teeth */}
                    <rect x="18" y="12" width="4" height="4" fill="var(--color-turquoise)"/>
                    <rect x="18" y="32" width="4" height="4" fill="var(--color-turquoise)"/>
                    <rect x="8" y="22" width="4" height="4" fill="var(--color-turquoise)"/>
                    <rect x="28" y="22" width="4" height="4" fill="var(--color-turquoise)"/>
                  </g>
                  <g className="gear gear-small">
                    <circle cx="34" cy="18" r="6" fill="none" stroke="var(--color-seafoam)" strokeWidth="2"/>
                    <circle cx="34" cy="18" r="2" fill="var(--color-navy)"/>
                  </g>
                  {/* Friction sparks */}
                  <circle className="spark sp1" cx="28" cy="20" r="1.5" fill="var(--color-yellow)"/>
                  <circle className="spark sp2" cx="30" cy="22" r="1" fill="var(--color-yellow)"/>
                  {/* Hand */}
                  <path className="manual-hand" d="M36,32 L42,26 L44,28 L38,38 L32,40 Z" fill="var(--color-pearl)" stroke="var(--color-navy)" strokeWidth="1"/>
                </svg>
              </div>
              <div className="sign-content">
                <h4>Manual everything</h4>
                <p>Simple tasks that should be automated are eating up your team's time.</p>
              </div>
            </div>

            {/* Forecasting by Feel */}
            <div className="sign-card-v2" style={{ '--delay': '0.6s' } as React.CSSProperties}>
              <div className="sign-icon-v2" data-sign="forecast">
                <svg viewBox="0 0 48 48">
                  {/* Crystal ball */}
                  <ellipse className="ball-base" cx="24" cy="42" rx="12" ry="3" fill="var(--color-navy)"/>
                  <circle className="crystal" cx="24" cy="24" r="14" fill="var(--color-seafoam)" opacity="0.5"/>
                  <circle className="crystal-inner" cx="24" cy="24" r="14" fill="none" stroke="var(--color-turquoise)" strokeWidth="2"/>
                  {/* Foggy question */}
                  <text className="fog-question" x="24" y="28" textAnchor="middle" fontSize="14" fontWeight="bold" fill="var(--color-navy)" opacity="0.5">?</text>
                  {/* Swirls */}
                  <path className="swirl sw1" d="M18,20 Q24,16 28,22" fill="none" stroke="var(--color-turquoise)" strokeWidth="1" opacity="0.6"/>
                  <path className="swirl sw2" d="M20,28 Q26,32 30,26" fill="none" stroke="var(--color-turquoise)" strokeWidth="1" opacity="0.6"/>
                </svg>
              </div>
              <div className="sign-content">
                <h4>Forecasting by feel</h4>
                <p>Planning and budgeting feels more like guessing than science.</p>
              </div>
            </div>

            {/* Hiring Bottleneck */}
            <div className="sign-card-v2" style={{ '--delay': '0.7s' } as React.CSSProperties}>
              <div className="sign-icon-v2" data-sign="hiring">
                <svg viewBox="0 0 48 48">
                  {/* Magnifying glass */}
                  <circle className="mag-glass" cx="20" cy="20" r="12" fill="var(--color-pearl)" stroke="var(--color-navy)" strokeWidth="2"/>
                  <line className="mag-handle" x1="29" y1="29" x2="40" y2="40" stroke="var(--color-navy)" strokeWidth="4" strokeLinecap="round"/>
                  {/* Person silhouette inside (dashed/incomplete) */}
                  <circle className="search-head" cx="20" cy="16" r="4" fill="none" stroke="var(--color-turquoise)" strokeWidth="1.5" strokeDasharray="3 2"/>
                  <path className="search-body" d="M20,20 L20,28 M14,24 L26,24" stroke="var(--color-turquoise)" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 2"/>
                  {/* X mark */}
                  <g className="no-result">
                    <line x1="36" y1="10" x2="42" y2="16" stroke="var(--color-yellow)" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="42" y1="10" x2="36" y2="16" stroke="var(--color-yellow)" strokeWidth="2" strokeLinecap="round"/>
                  </g>
                </svg>
              </div>
              <div className="sign-content">
                <h4>Hiring bottleneck</h4>
                <p>You've tried to hire a data person but can't find the right fit or justify full-time.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section - Data Constellation Theme */}
      <section className="section industries-section">
        <div className="industries-bg">
          {/* Floating data characters */}
          <div className="float-chars">
            <span className="float-char fc1">#</span>
            <span className="float-char fc2">@</span>
            <span className="float-char fc3">*</span>
            <span className="float-char fc4">&amp;</span>
            <span className="float-char fc5">%</span>
            <span className="float-char float-square fc6"></span>
            <span className="float-char fc7">#</span>
            <span className="float-char fc8">@</span>
            <span className="float-char fc9">*</span>
            <span className="float-char fc10">&amp;</span>
            <span className="float-char fc11">%</span>
            <span className="float-char float-square fc12"></span>
          </div>
          {/* Plot dots */}
          <div className="plot-dots">
            <span className="plot-dot pd1"></span>
            <span className="plot-dot pd2"></span>
            <span className="plot-dot pd3"></span>
            <span className="plot-dot pd4"></span>
            <span className="plot-dot pd5"></span>
            <span className="plot-dot pd6"></span>
            <span className="plot-dot pd7"></span>
            <span className="plot-dot pd8"></span>
            <span className="plot-dot pd9"></span>
            <span className="plot-dot pd10"></span>
            <span className="plot-dot pd11"></span>
            <span className="plot-dot pd12"></span>
            <span className="plot-dot pd13"></span>
            <span className="plot-dot pd14"></span>
            <span className="plot-dot pd15"></span>
          </div>
        </div>
        <div className="container industries-container">
          <div className="industries-header">
            <div className="accent-bar accent-bar-center mb-lg"></div>
            <h2>Industries we work with</h2>
            <p>Our approach works across industries. We've helped companies in:</p>
          </div>

          <div className="industries-grid-v2">
            {/* E-commerce */}
            <div className="industry-card-v2" style={{ '--delay': '0s' } as React.CSSProperties}>
              <h3>E-commerce & Retail</h3>
              <p>Sales analytics, inventory planning, customer insights.</p>
            </div>

            {/* SaaS */}
            <div className="industry-card-v2" style={{ '--delay': '0.1s' } as React.CSSProperties}>
              <h3>SaaS & Technology</h3>
              <p>Product analytics, churn prediction, growth metrics.</p>
            </div>

            {/* Healthcare */}
            <div className="industry-card-v2" style={{ '--delay': '0.2s' } as React.CSSProperties}>
              <h3>Healthcare Services</h3>
              <p>Operational efficiency, patient flow, compliance reporting.</p>
            </div>

            {/* Manufacturing */}
            <div className="industry-card-v2" style={{ '--delay': '0.3s' } as React.CSSProperties}>
              <h3>Manufacturing</h3>
              <p>Production tracking, supply chain visibility, quality metrics.</p>
            </div>

            {/* Professional Services */}
            <div className="industry-card-v2" style={{ '--delay': '0.4s' } as React.CSSProperties}>
              <h3>Professional Services</h3>
              <p>Utilization tracking, project profitability, resource planning.</p>
            </div>

            {/* Financial Services */}
            <div className="industry-card-v2" style={{ '--delay': '0.5s' } as React.CSSProperties}>
              <h3>Financial Services</h3>
              <p>Risk analytics, portfolio tracking, regulatory reporting.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Not Right For Section */}
      <section className="section not-for-section-v2">
        <div className="not-for-bg">
          <svg className="not-for-wave" viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path d="M0,60 Q360,120 720,60 T1440,60 L1440,120 L0,120 Z" fill="var(--color-sand)" opacity="0.5"/>
          </svg>
        </div>
        <div className="container not-for-container">
          <div className="not-for-header-v2">
            <h2>We might not be the right fit if...</h2>
            <p>Being honest about fit helps everyone. We're probably not the best choice if:</p>
          </div>

          <div className="not-for-grid-v2">
            <div className="not-for-item-v2" style={{ '--delay': '0s' } as React.CSSProperties}>
              <div className="not-for-x">
                <svg viewBox="0 0 24 24">
                  <line className="x-line x1" x1="6" y1="6" x2="18" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                  <line className="x-line x2" x1="18" y1="6" x2="6" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </div>
              <span>You need cutting-edge ML research</span>
            </div>
            <div className="not-for-item-v2" style={{ '--delay': '0.1s' } as React.CSSProperties}>
              <div className="not-for-x">
                <svg viewBox="0 0 24 24">
                  <line className="x-line x1" x1="6" y1="6" x2="18" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                  <line className="x-line x2" x1="18" y1="6" x2="6" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </div>
              <span>You have a large in-house data team already</span>
            </div>
            <div className="not-for-item-v2" style={{ '--delay': '0.2s' } as React.CSSProperties}>
              <div className="not-for-x">
                <svg viewBox="0 0 24 24">
                  <line className="x-line x1" x1="6" y1="6" x2="18" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                  <line className="x-line x2" x1="18" y1="6" x2="6" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </div>
              <span>You're looking for one-off analysis only</span>
            </div>
            <div className="not-for-item-v2" style={{ '--delay': '0.3s' } as React.CSSProperties}>
              <div className="not-for-x">
                <svg viewBox="0 0 24 24">
                  <line className="x-line x1" x1="6" y1="6" x2="18" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                  <line className="x-line x2" x1="18" y1="6" x2="6" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </div>
              <span>You need industry-specific regulatory expertise</span>
            </div>
            <div className="not-for-item-v2" style={{ '--delay': '0.4s' } as React.CSSProperties}>
              <div className="not-for-x">
                <svg viewBox="0 0 24 24">
                  <line className="x-line x1" x1="6" y1="6" x2="18" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                  <line className="x-line x2" x1="18" y1="6" x2="6" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </div>
              <span>You're pre-revenue or very early stage</span>
            </div>
            <div className="not-for-item-v2" style={{ '--delay': '0.5s' } as React.CSSProperties}>
              <div className="not-for-x">
                <svg viewBox="0 0 24 24">
                  <line className="x-line x1" x1="6" y1="6" x2="18" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                  <line className="x-line x2" x1="18" y1="6" x2="6" y2="18" stroke="var(--color-navy)" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </div>
              <span>You need enterprise-scale data engineering</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Split Card Design */}
      <section className="section wif-cta-v2">
        <div className="container">
          <div className="wif-cta-card">
            <div className="wif-cta-card-visual">
              <div className="wif-cta-circles">
                <div className="wif-cta-circle cc-1"></div>
                <div className="wif-cta-circle cc-2"></div>
                <div className="wif-cta-circle cc-3"></div>
              </div>
              <div className="wif-cta-checkmark">
                <svg viewBox="0 0 60 60">
                  <circle className="check-circle" cx="30" cy="30" r="25" fill="none" stroke="var(--color-white)" strokeWidth="2"/>
                  <path className="check-path" d="M18,30 L26,38 L42,22" fill="none" stroke="var(--color-white)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className="wif-cta-card-content">
              <h2>Think we might be a fit?</h2>
              <p>
                Let's find out. Book a discovery call and we'll honestly assess 
                whether we can help — no pressure, no obligation.
              </p>
              <div className="wif-cta-v2-buttons">
                <Link to="/contact" className="btn btn-white btn-lg">
                  Schedule a Call
                </Link>
                <Link to="/how-it-works" className="wif-cta-link">
                  See How It Works
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default WhoItsFor;
